import { NotificationService } from 'src/app/shared/services/notification.service';
import { inject, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { ConfirmationService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { FlexiComplianceTemplateService } from './flexi-compliance-template.service';
import {
  AddTemplateItemRequest,
  AssignTemplateToBusinessRequest,
  FlexiComplianceBusinessTemplate,
  FlexiComplianceCatalogItem,
  FlexiComplianceTemplate,
  FlexiComplianceTemplateSection,
  UpdateComplianceTemplateRequest
} from './flexi-compliance-template.models';

/**
 * Admin editor for a single compliance template. Three panels:
 *   1. Metadata — name, description, active toggle.
 *   2. Items by section — remove existing items + inline form to add a
 *      catalog item (by catalog item ID) or a custom item (free text).
 *   3. Business assignments — list current assignments, mark default,
 *      unassign, and add a new assignment by Business ID. v1 deliberately
 *      avoids a business search/picker — admins paste IDs they already know.
 *      A richer picker can layer on later without changing the backend.
 *
 * Route: <c>.../Flexi/compliance/templates/:id</c>. Auth lives server-side
 * (TemplateAuthorPolicy: Admin / any HSE advisor). The FE renders
 * the page unconditionally and surfaces 401/403 as a clean access-denied
 * state so users who shouldn't be here see something readable instead of
 * a blank page.
 */
@Component({
  selector: 'flexi-compliance-templates-editor',
  templateUrl: './templates-editor.component.html',
  styleUrls: ['./templates.scss'],
  standalone: false
})
export class TemplatesEditorComponent implements OnInit {
  private readonly notifications = inject(NotificationService);

  template: FlexiComplianceTemplate | null = null;
  assignments: FlexiComplianceBusinessTemplate[] = [];

  /** True once a load call has been rejected with 401/403; renders an access-denied state. */
  accessDenied = false;
  loading = false;
  loadError: string | null = null;

  // Metadata form state.
  draftName = '';
  draftDescription = '';
  draftActive = true;
  savingMeta = false;

  // Per-section "add custom item" inline form, keyed by sectionId.
  addCustomOpen = new Set<number>();
  addCustomInput: Record<number, { question: string; ref: string; weight: number }> = {};

  // Per-section "add standard question" inline picker.
  addCatalogOpen = new Set<number>();
  addCatalogInput: Record<number, { catalogItemId: number | null; weight: number }> = {};

  /**
   * The version's catalog questions, keyed by catalog section id — the options
   * behind the picker. Loaded once alongside the template.
   *
   * This control used to be a bare numeric input asking for a "Catalog item
   * ID": a database key nobody outside the dev team could know, which made the
   * affordance unusable. The endpoint was already there for the template
   * wizard; the editor simply never called it.
   */
  catalogItemsBySection: Record<number, FlexiComplianceCatalogItem[]> = {};
  catalogLoading = false;

  busyItemIds = new Set<number>();
  busySectionIds = new Set<number>();

  /**
   * Business context from the <c>?businessId</c> query param (set when the
   * editor was reached from a business's rating page). Passed on every
   * mutation so the backend can copy-on-write shared templates: the fork
   * gets the edit + this business's assignment, and we redirect to it.
   * Absent when the editor is opened from the global library — edits then
   * apply in place and the shared-warning banner does the explaining.
   */
  businessContextId: number | null = null;

  /** Transient banner shown right after a copy-on-write fork redirect. */
  forkedNotice = false;

  // "Add topic" inline form.
  addTopicOpen = false;
  addTopicTitle = '';
  addingTopic = false;

  // Per-section rename form (custom topics only), keyed by sectionId.
  renameTopicId: number | null = null;
  renameTopicTitle = '';
  renamingTopic = false;

  /**
   * Computed snapshot of catalog section IDs that are currently visible on
   * this template. Anything in the template's CatalogVersion but missing
   * from <c>template.sections</c> is considered excluded — the editor uses
   * this to render the "Restore section" affordance.
   *
   * Populated by GET /flexi/compliance/templates/{id}/catalog-sections (not
   * built yet); for v1 we don't surface restore in the UI — admin can
   * re-add items one by one, which implicitly puts the section back as the
   * exclusion row is cleared via a restore call (or, until the BE adds
   * auto-clear, by hitting the explicit endpoint from a curl).
   */
  excludedSectionIds: Set<number> = new Set();

  // Business-assignment panel state.
  newAssignBusinessId: number | null = null;
  newAssignDefault = false;
  assigning = false;
  busyAssignmentIds = new Set<number>();
  assignmentsError: string | null = null;

  /**
   * Display label for a section: the translated key when one actually resolves,
   * otherwise the stored title.
   *
   * The previous `(translateKey | translate) || title` could never fall back —
   * ngx-translate echoes the KEY back when it has no entry, so the left side
   * was always truthy and users saw raw strings like
   * `COMPLIANCE.SECTIONS.SAFETY_REP_AMU`. Comparing the result against the key
   * is the only way to detect a miss.
   *
   * Titles are already stored as readable text (and custom topics have no key
   * at all), so the fallback is the correct answer, not a placeholder.
   */
  sectionLabel(section: { translateKey?: string | null; title?: string | null; }): string {
    const key = section.translateKey?.trim();
    if (key) {
      const translated = this.translate.instant(key);
      if (translated && translated !== key) {
        return translated;
      }
    }
    return section.title ?? '';
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: FlexiComplianceTemplateService,
    private confirm: ConfirmationService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const templateId = idParam ? Number(idParam) : NaN;
    if (!Number.isFinite(templateId)) {
      this.loadError = 'Missing template id.';
      return;
    }
    const bizParam = this.route.snapshot.queryParamMap.get('businessId');
    this.businessContextId = bizParam ? Number(bizParam) : null;
    if (!Number.isFinite(this.businessContextId as number)) this.businessContextId = null;
    this.loadTemplate(templateId);
  }

  /** True when this template feeds 2+ businesses — surfaced as a banner. */
  get isShared(): boolean {
    return (this.template?.businessAssignmentCount ?? 0) >= 2;
  }

  /**
   * Central post-mutation handler. When the backend copy-on-write forked
   * the template, the response carries the CLONE's id — swap the route to
   * it (keeping the business context) and show the forked notice so the
   * author understands why the URL changed.
   */
  private applyResult(t: FlexiComplianceTemplate): void {
    const forked = this.template !== null && t.id !== this.template.id;
    this.template = t;
    this.draftName = t.name;
    this.draftDescription = t.description ?? '';
    this.draftActive = t.isActive;
    if (forked) {
      this.forkedNotice = true;
      this.router.navigate(['../', t.id], {
        relativeTo: this.route,
        queryParams: this.businessContextId ? { businessId: this.businessContextId } : {},
        replaceUrl: true
      });
    }
  }

  dismissForkedNotice(): void { this.forkedNotice = false; }

  // ---------- Load ----------

  private loadTemplate(templateId: number): void {
    this.loading = true;
    this.loadError = null;
    this.accessDenied = false;
    this.service.get(templateId).subscribe({
      next: t => {
        this.template = t;
        this.draftName = t.name;
        this.draftDescription = t.description ?? '';
        this.draftActive = t.isActive;
        this.loading = false;
        // The picker's options. Needs the version id, so it can only start
        // once the template has resolved.
        this.loadCatalog(t.catalogVersionId);
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        if (err?.status === 401 || err?.status === 403) {
          this.accessDenied = true;
          return;
        }
        this.loadError = err?.error?.detail
          ?? this.translate.instant('FLEXI_COMPLIANCE_TEMPLATE.ERRORS.LOAD_FAILED');
      }
    });
    // Load assignments in parallel; surface its own error so it doesn't
    // mask the main editor.
    this.reloadAssignments(templateId);
  }

  private reloadAssignments(templateId: number): void {
    // Assignments are scoped by business in the API, not by template — we
    // need to pull all template rows and filter, but the BE list endpoint
    // is per-business. For v1 we just show the businessAssignmentCount in
    // the metadata header and surface per-business assignment management
    // from the business side. Leave this list empty in the editor unless we
    // add a "GET /templates/:id/businesses" endpoint later.
    this.assignments = [];
    void templateId;
  }

  // ---------- Metadata ----------

  saveMeta(): void {
    if (!this.template || !this.draftName.trim()) return;
    this.savingMeta = true;
    const body: UpdateComplianceTemplateRequest = {
      name: this.draftName.trim(),
      description: this.draftDescription.trim() || null,
      isActive: this.draftActive
    };
    this.service.update(this.template.id, body, this.businessContextId).subscribe({
      next: t => {
        this.notifications.success('UI_NOTIFICATIONS.SAVED');
        this.applyResult(t);
        this.savingMeta = false;
      },
      error: (err: HttpErrorResponse) => {
        this.savingMeta = false;
        this.loadError = err?.error?.detail
          ?? this.translate.instant('FLEXI_COMPLIANCE_TEMPLATE.ERRORS.UPDATE_FAILED');
      }
    });
  }

  // ---------- Items ----------

  openAddCustom(sectionId: number): void {
    this.addCustomOpen.add(sectionId);
    this.addCustomInput[sectionId] = { question: '', ref: '', weight: 1 };
  }

  cancelAddCustom(sectionId: number): void {
    this.addCustomOpen.delete(sectionId);
    delete this.addCustomInput[sectionId];
  }

  submitAddCustom(sectionId: number): void {
    if (!this.template) return;
    const draft = this.addCustomInput[sectionId];
    if (!draft || !draft.question.trim()) return;
    const body: AddTemplateItemRequest = {
      sectionId,
      catalogItemId: null,
      question: draft.question.trim(),
      regulatoryReference: draft.ref.trim() || null,
      weight: draft.weight || 1
    };
    this.service.addItem(this.template.id, body, this.businessContextId).subscribe({
      next: t => {
        this.notifications.success('UI_NOTIFICATIONS.SAVED');
        this.applyResult(t);
        this.cancelAddCustom(sectionId);
      },
      error: (err: HttpErrorResponse) => {
        this.loadError = err?.error?.detail
          ?? this.translate.instant('FLEXI_COMPLIANCE_TEMPLATE.ERRORS.ADD_ITEM_FAILED');
      }
    });
  }

  openAddCatalog(sectionId: number): void {
    this.addCatalogOpen.add(sectionId);
    this.addCatalogInput[sectionId] = { catalogItemId: null, weight: 1 };
  }

  cancelAddCatalog(sectionId: number): void {
    this.addCatalogOpen.delete(sectionId);
    delete this.addCatalogInput[sectionId];
  }

  /**
   * Loads the version's catalog so the picker can offer real questions.
   *
   * Failure is deliberately quiet: the catalog only powers an optional
   * affordance, and blocking the whole editor because a dropdown could not be
   * filled would be a worse outcome than the button being empty. `catalogEmpty`
   * lets the template say so instead of showing a dead select.
   */
  private loadCatalog(catalogVersionId: number): void {
    this.catalogLoading = true;
    this.service.getCatalogStructure(catalogVersionId).subscribe({
      next: structure => {
        this.catalogItemsBySection = {};
        for (const section of structure.sections ?? []) {
          this.catalogItemsBySection[section.id] = section.items ?? [];
        }
        this.catalogLoading = false;
      },
      error: () => {
        this.catalogItemsBySection = {};
        this.catalogLoading = false;
      }
    });
  }

  /**
   * Catalog questions still available for a section — the section's catalog
   * items minus the ones the template already holds.
   *
   * Filtering matters: without it the list re-offers questions that are
   * already in the template, and adding one again is either a duplicate or a
   * server-side rejection.
   */
  availableCatalogItems(section: FlexiComplianceTemplateSection): FlexiComplianceCatalogItem[] {
    const all = this.catalogItemsBySection[section.sectionId] ?? [];
    const taken = new Set(
      section.items
        .map(i => i.catalogItemId)
        .filter((id): id is number => typeof id === 'number')
    );
    return all.filter(i => !taken.has(i.id));
  }

  /** True when every catalog question for this section is already in place. */
  catalogExhausted(section: FlexiComplianceTemplateSection): boolean {
    return ( !this.catalogLoading && this.availableCatalogItems(section).length === 0
    );
  }

  /**
   * Weight defaults to the catalog's own weight for the chosen question rather
   * than a hardcoded 1, so adding a standard item back reproduces how it was
   * originally scored.
   */
  onCatalogItemPicked(section: FlexiComplianceTemplateSection): void {
    const draft = this.addCatalogInput[section.sectionId];
    if (!draft?.catalogItemId) return;
    const picked = this.availableCatalogItems(section)
      .find(i => i.id === draft.catalogItemId);
    if (picked) draft.weight = picked.weight;
  }

  submitAddCatalog(sectionId: number): void {
    if (!this.template) return;
    const draft = this.addCatalogInput[sectionId];
    if (!draft || !draft.catalogItemId) return;
    const body: AddTemplateItemRequest = {
      sectionId,
      catalogItemId: draft.catalogItemId,
      weight: draft.weight || 1
    };
    this.service.addItem(this.template.id, body, this.businessContextId).subscribe({
      next: t => {
        this.notifications.success('UI_NOTIFICATIONS.SAVED');
        this.applyResult(t);
        this.cancelAddCatalog(sectionId);
      },
      error: (err: HttpErrorResponse) => {
        this.loadError = err?.error?.detail
          ?? this.translate.instant('FLEXI_COMPLIANCE_TEMPLATE.ERRORS.ADD_ITEM_FAILED');
      }
    });
  }

  removeItem(itemId: number): void {
    if (!this.template) return;
    this.confirm.confirm({
      message: this.translate.instant('FLEXI_COMPLIANCE_TEMPLATE.CONFIRM.REMOVE_ITEM'),
      accept: () => {
        const templateId = this.template!.id;
        this.busyItemIds.add(itemId);
        this.service.removeItem(templateId, itemId, this.businessContextId).subscribe({
          next: t => {
            this.busyItemIds.delete(itemId);
            this.applyResult(t);
          },
          error: (err: HttpErrorResponse) => {
            this.busyItemIds.delete(itemId);
            this.loadError = err?.error?.detail
              ?? this.translate.instant('FLEXI_COMPLIANCE_TEMPLATE.ERRORS.REMOVE_ITEM_FAILED');
          }
        });
      }
    });
  }

  // ---------- Sections ----------

  removeSection(sectionId: number): void {
    if (!this.template) return;
    this.confirm.confirm({
      message: this.translate.instant('FLEXI_COMPLIANCE_TEMPLATE.CONFIRM.REMOVE_SECTION'),
      accept: () => {
        const templateId = this.template!.id;
        this.busySectionIds.add(sectionId);
        this.service.removeSection(templateId, sectionId, this.businessContextId).subscribe({
          next: t => {
            this.busySectionIds.delete(sectionId);
            this.applyResult(t);
            this.excludedSectionIds.add(sectionId);
          },
          error: (err: HttpErrorResponse) => {
            this.busySectionIds.delete(sectionId);
            this.loadError = err?.error?.detail
              ?? this.translate.instant('FLEXI_COMPLIANCE_TEMPLATE.ERRORS.REMOVE_SECTION_FAILED');
          }
        });
      }
    });
  }

  // ---------- Custom topics ----------

  openAddTopic(): void {
    this.addTopicOpen = true;
    this.addTopicTitle = '';
  }

  cancelAddTopic(): void {
    this.addTopicOpen = false;
    this.addTopicTitle = '';
  }

  submitAddTopic(): void {
    if (!this.template || !this.addTopicTitle.trim() || this.addingTopic) return;
    this.addingTopic = true;
    this.service.addSection(this.template.id, this.addTopicTitle.trim(), this.businessContextId).subscribe({
      next: t => {
        this.notifications.success('UI_NOTIFICATIONS.SAVED');
        this.addingTopic = false;
        this.cancelAddTopic();
        this.applyResult(t);
      },
      error: (err: HttpErrorResponse) => {
        this.addingTopic = false;
        this.loadError = err?.error?.detail
          ?? this.translate.instant('FLEXI_COMPLIANCE_TEMPLATE.ERRORS.ADD_TOPIC_FAILED');
      }
    });
  }

  openRenameTopic(sectionId: number, currentTitle: string): void {
    this.renameTopicId = sectionId;
    this.renameTopicTitle = currentTitle;
  }

  cancelRenameTopic(): void {
    this.renameTopicId = null;
    this.renameTopicTitle = '';
  }

  submitRenameTopic(): void {
    if (!this.template || this.renameTopicId === null || !this.renameTopicTitle.trim() || this.renamingTopic) return;
    this.renamingTopic = true;
    this.service.renameSection(this.template.id, this.renameTopicId, this.renameTopicTitle.trim(), this.businessContextId).subscribe({
      next: t => {
        this.renamingTopic = false;
        this.cancelRenameTopic();
        this.applyResult(t);
      },
      error: (err: HttpErrorResponse) => {
        this.renamingTopic = false;
        this.loadError = err?.error?.detail
          ?? this.translate.instant('FLEXI_COMPLIANCE_TEMPLATE.ERRORS.RENAME_TOPIC_FAILED');
      }
    });
  }

  // ---------- Business assignments ----------

  submitAssign(): void {
    if (!this.template || !this.newAssignBusinessId) return;
    this.assigning = true;
    this.assignmentsError = null;
    const body: AssignTemplateToBusinessRequest = {
      templateId: this.template.id,
      isDefault: this.newAssignDefault
    };
    this.service.assign(this.newAssignBusinessId, body).subscribe({
      next: row => {
        this.assigning = false;
        this.newAssignBusinessId = null;
        this.newAssignDefault = false;
        // Show the freshly added row at the top — even if the per-template
        // list endpoint isn't built yet, the user gets confirmation.
        this.assignments = [row, ...this.assignments.filter(a => a.id !== row.id)];
      },
      error: (err: HttpErrorResponse) => {
        this.assigning = false;
        this.assignmentsError = err?.error?.detail
          ?? this.translate.instant('FLEXI_COMPLIANCE_TEMPLATE.ERRORS.ASSIGN_FAILED');
      }
    });
  }

  unassign(row: FlexiComplianceBusinessTemplate): void {
    this.confirm.confirm({
      message: this.translate.instant('FLEXI_COMPLIANCE_TEMPLATE.CONFIRM.UNASSIGN'),
      accept: () => {
        this.busyAssignmentIds.add(row.id);
        this.service.unassign(row.businessId, row.templateId).subscribe({
          next: () => {
            this.busyAssignmentIds.delete(row.id);
            this.assignments = this.assignments.filter(a => a.id !== row.id);
          },
          error: (err: HttpErrorResponse) => {
            this.busyAssignmentIds.delete(row.id);
            this.assignmentsError = err?.error?.detail
              ?? this.translate.instant('FLEXI_COMPLIANCE_TEMPLATE.ERRORS.UNASSIGN_FAILED');
          }
        });
      }
    });
  }

  backToList(): void {
    this.router.navigate(['../'], { relativeTo: this.route });
  }
}
